import React from "react";
import { motion } from "framer-motion";
import { Shield, Target, Award, Users, History, TrendingUp } from "lucide-react";
import { IMAGES } from "@/assets/images";
import { companyStats } from "@/data/index";
import { StatsCard } from "@/components/Cards";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

export default function CompanyEs() {
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src={IMAGES.ALUMINUM_PRODUCTS_3} 
            alt="Fondo Corporativo" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.smooth}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 italic-none">
              Damos Forma al Futuro con Aluminio
            </h1>
            <p className="text-lg md:text-xl opacity-90 leading-relaxed">
              Con más de 20 años de experiencia, ofrecemos soluciones de aluminio de alta tecnología 
              a los proyectos líderes de Turquía y el mundo. Producimos con precisión en nuestras 
              instalaciones integradas.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Profile Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="text-sm font-bold tracking-widest text-primary uppercase mb-4">
                Perfil de la Empresa
              </h2>
              <h3 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                La Dirección de Confianza y Tecnología en el Sector
              </h3>
              <div className="space-y-6 text-muted-foreground leading-relaxed">
                <p>
                  Nuestra fábrica se encuentra entre los pioneros del sector de extrusión de aluminio 
                  con 26.000 m² de área cerrada y una capacidad de producción anual de 27.000 toneladas. 
                  Servimos en una estructura completamente integrada desde la fundición hasta el taller 
                  de moldes, desde las líneas de extrusión hasta las instalaciones de tratamiento superficial.
                </p>
                <p>
                  Como socio de soluciones en una amplia gama desde sistemas arquitectónicos hasta 
                  automotriz, desde energía hasta electrodomésticos, producimos cada perfil según 
                  estándares de calidad internacional (ISO, Qualicoat, Qualanod). Con nuestra visión 
                  orientada a la exportación, llevamos valor a más de 45 países.
                </p>
              </div>
              <div className="mt-10 grid grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-full">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium">100% Control de Calidad</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-full">
                    <Award className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium">Certificados Globales</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={IMAGES.EKRAN_G_R_NT_S_2026_02_07_202330_81} 
                alt="Instalación de Producción" 
                className="w-full aspect-video lg:aspect-square object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl hidden md:block border border-border">
                <div className="flex items-center gap-4">
                  <TrendingUp className="w-10 h-10 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Crecimiento Anual</p>
                    <p className="text-2xl font-bold text-primary">+15%</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Grid */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {companyStats.map((stat) => (
              <motion.div key={stat.id} variants={staggerItem}>
                <StatsCard stat={stat} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Vision & Values */}
      <section className="py-24 bg-background border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground flex items-center justify-center rounded-lg">
                <Target className="w-6 h-6" />
              </div>
              <h4 className="text-xl font-bold">Nuestra Visión</h4>
              <p className="text-muted-foreground">
                Convertirnos en una marca global en tecnologías de aluminio, construyendo 
                el mundo del futuro con soluciones sostenibles e innovadoras.
              </p>
            </div>
            <div className="space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground flex items-center justify-center rounded-lg">
                <Users className="w-6 h-6" />
              </div>
              <h4 className="text-xl font-bold">Nuestra Misión</h4>
              <p className="text-muted-foreground">
                Agregar valor al sector con procesos de producción que satisfagan las expectativas 
                del cliente al más alto nivel, respetuosos con el medio ambiente y centrados en la ingeniería.
              </p>
            </div>
            <div className="space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground flex items-center justify-center rounded-lg">
                <Award className="w-6 h-6" />
              </div>
              <h4 className="text-xl font-bold">Nuestros Valores</h4>
              <p className="text-muted-foreground">
                Honestidad, innovación, pasión por la calidad e inversión en las personas. 
                Para nosotros, el éxito no son solo números, sino la confianza que creamos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* History Timeline */}
      <section className="py-24 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <History className="w-10 h-10 text-primary mx-auto mb-4 opacity-50" />
            <h2 className="text-3xl font-bold mb-4">Nuestro Viaje</h2>
            <p className="text-muted-foreground">
              Nuestra historia de un cuarto de siglo llena de éxito y determinación, 
              desde un pequeño taller hasta una gran instalación integrada.
            </p>
          </div>
          
          <div className="space-y-12 max-w-4xl mx-auto">
            {[ 
              { year: "2002", text: "Se pusieron los cimientos de la primera instalación de producción y se puso en funcionamiento la línea de extrusión de 4\"." },
              { year: "2010", text: "La capacidad de producción anual se incrementó a 10.000 toneladas, se realizó la primera exportación a Europa." },
              { year: "2018", text: "Se completaron las inversiones en instalaciones de pintura en polvo vertical completamente integradas y líneas de alta capacidad de 8\"." },
              { year: "2026", text: "Con la transformación digital y la integración de energía verde, se convirtió en una de las instalaciones más modernas del sector." }
            ].map((item, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex gap-6 items-start"
              >
                <div className="text-2xl font-mono font-bold text-primary shrink-0 pt-1">{item.year}</div>
                <div className="pb-8 border-l-2 border-primary/20 pl-8">
                  <p className="text-lg text-foreground">{item.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6 italic-none">Conózcanos</h2>
          <p className="mb-10 opacity-90 max-w-xl mx-auto">
            Si desea visitar nuestras instalaciones u obtener soporte técnico para sus proyectos, 
            nuestro equipo experto siempre está listo para ayudar.
          </p>
          <button className="bg-white text-primary px-8 py-3 rounded-md font-bold hover:bg-opacity-90 transition-colors">
            Póngase en Contacto
          </button>
        </div>
      </section>
    </div>
  );
}